import { ability } from '@/plugins/casl/ability'
import { disconnectEcho } from '@/plugins/echo'
import { useApi } from '@/utils/api'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'

export const useAuthStore = defineStore('auth', () => {
  // =========================================================================
  // COOKIES — nama konsisten dengan guards.js
  // =========================================================================
  const userDataCookie = useCookie('userData')
  const accessTokenCookie = useCookie('accessToken')

  // Regresi Modul D: ability array (userAbilityRules) dulu cookie, bisa
  // >4KB (mis. role manager 72 permission) dan lewat batas ukuran cookie
  // browser -> DIAM-DIAM gagal tersimpan. Pindah ke localStorage.
  const userAbilityRulesStorage = useLocalStorage('userAbilityRules', [])

  // §13.1 — info tampilan selama sesi impersonate aktif (ImpersonateBanner).
  // null = tidak sedang impersonate. Backend (POST .../impersonate,
  // POST /impersonate/stop) sudah menegakkan BR-016 penuh; ini murni state
  // UI, bukan sumber kebenaran otorisasi.
  const impersonationInfo = useLocalStorage('impersonationInfo', null)

  // =========================================================================
  // STATE (reactive dari cookie)
  // =========================================================================
  const user = computed({
    get: () => userDataCookie.value,
    set: val => { userDataCookie.value = val },
  })

  const token = computed({
    get: () => accessTokenCookie.value,
    set: val => { accessTokenCookie.value = val },
  })

  const abilities = computed({
    get: () => userAbilityRulesStorage.value ?? [],
    set: val => { userAbilityRulesStorage.value = val },
  })

  // =========================================================================
  // GETTERS
  // =========================================================================
  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const isImpersonating = computed(() => !!impersonationInfo.value)

  // =========================================================================
  // ACTIONS
  // =========================================================================

  /**
   * Login — kirim credentials ke /api/v1/auth/login
   *
   * ✅ FIX: useApi di utils/api.js pakai ofetch.
   * ofetch membaca body dari key `body`, bukan `data`.
   * Pass objek langsung ke post({ body: credentials }).
   */
  const login = async credentials => {
    const data = await useApi('/api/v1/auth/login').post({ body: credentials })

    // ✅ FIX: ofetch mengembalikan response langsung (tidak dibungkus { data })
    // Backend kita: ApiResponse::success({ user, token, userAbilities })
    // → ofetch sudah auto-parse JSON, hasilnya adalah object response penuh
    const responseData = data?.data ?? data

    // Set cookies (jalur normal via reactive useCookie)
    userDataCookie.value = responseData.user
    accessTokenCookie.value = responseData.token

    // localStorage — bukan cookie, jadi tidak kena masalah ukuran ~4KB &
    // watcher-async yang dijelaskan di bawah untuk cookie. useLocalStorage
    // (VueUse) menulis langsung ke localStorage.setItem() di dalam setter-nya
    // sendiri, jadi tidak perlu fallback manual.
    userAbilityRulesStorage.value = responseData.userAbilities ?? []

    // ⚠️ FALLBACK: tulis manual langsung ke document.cookie, JANGAN cuma
    // andalkan watcher useCookie(). Untuk value object (userData) — watcher-nya
    // bersifat async dan bisa gagal diam-diam untuk tipe non-string; dan untuk
    // value yang PERSIS SAMA dengan value lama, Vue MENGANGGAP TIDAK ADA
    // PERUBAHAN dan watcher TIDAK PERNAH DIJALANKAN sama sekali. Menulis
    // manual ke document.cookie di bawah ini TIDAK bergantung pada reactivity
    // Vue sama sekali, jadi selalu jalan apapun kondisinya.
    const maxAge = 60 * 60 * 24 * 30

    document.cookie = `userData=${encodeURIComponent(JSON.stringify(responseData.user))}; path=/; max-age=${maxAge}`
    document.cookie = `accessToken=${encodeURIComponent(responseData.token)}; path=/; max-age=${maxAge}`

    // Verifikasi — kalau masih gagal, ini akan kelihatan jelas di console
    const verify = document.cookie.includes('userData=')
      && document.cookie.includes('accessToken=')

    if (!verify) {
      console.error('[AUTH] Gagal menyimpan salah satu cookie auth. document.cookie:', document.cookie)
    }

    ability.update(userAbilityRulesStorage.value)

    return responseData
  }

  const logout = async () => {
    try {
      // §13.1 BR-016.6: kalau sedang impersonate, backend sendiri sudah
      // menganggap logout ini sebagai "kembali ke admin" (impersonate-stop),
      // bukan logout token admin yang sesungguhnya — tapi token yang
      // dipegang browser saat ini (token impersonate) tetap dicabut server,
      // jadi dari sisi frontend perlakuannya tetap sama: bersihkan semua
      // state lokal termasuk penanda impersonate.
      await useApi('/api/v1/auth/logout').post({ body: {} })
    } catch (e) {
      console.warn('Logout API error:', e)
    } finally {
      userDataCookie.value = null
      accessTokenCookie.value = null
      userAbilityRulesStorage.value = []
      impersonationInfo.value = null

      // Module 07 — putus koneksi realtime (NavBarNotifications.vue private
      // channel) supaya tidak ada socket ter-otentikasi yang menggantung
      // setelah user logout.
      disconnectEcho()

      // Sama seperti di login() — jangan cuma andalkan watcher useCookie().
      // Hapus juga langsung lewat document.cookie supaya cookie fisik pasti
      // hilang, tidak bergantung pada apakah Vue menganggap value "berubah".
      document.cookie = 'userData=; path=/; max-age=-1'
      document.cookie = 'accessToken=; path=/; max-age=-1'

      ability.update([])
    }
  }

  const fetchUser = async () => {
    try {
      const data = await useApi('/api/v1/auth/me').get()
      const responseData = data?.data ?? data

      userDataCookie.value = responseData.user

      if (responseData.userAbilities) {
        userAbilityRulesStorage.value = responseData.userAbilities
        ability.update(responseData.userAbilities)
      }

      return responseData.user
    } catch (e) {
      userDataCookie.value = null
      accessTokenCookie.value = null
      userAbilityRulesStorage.value = []

      return null
    }
  }

  /**
   * §13.1 — mulai impersonate target user. Menukar cookie/localStorage aktif
   * ke identitas target (persis pola login()), sekaligus menandai
   * impersonationInfo supaya ImpersonateBanner tampil. Backend yang
   * menegakkan seluruh BR-016 (siapa boleh, target siapa, dst) — kalau
   * ditolak, promise ini reject dan tidak ada state lokal yang berubah.
   */
  const startImpersonation = async targetUserId => {
    const data = await useApi(`/api/v1/users/${targetUserId}/impersonate`).post({ body: {} })
    const responseData = data?.data ?? data

    const maxAge = 60 * 60 * 24 * 30

    userDataCookie.value = responseData.target_user
    accessTokenCookie.value = responseData.token
    userAbilityRulesStorage.value = responseData.userAbilities ?? []

    document.cookie = `userData=${encodeURIComponent(JSON.stringify(responseData.target_user))}; path=/; max-age=${maxAge}`
    document.cookie = `accessToken=${encodeURIComponent(responseData.token)}; path=/; max-age=${maxAge}`

    impersonationInfo.value = {
      targetName: responseData.target_user?.name,
      targetId: responseData.target_user?.id,
      startedAt: new Date().toISOString(),
    }

    ability.update(userAbilityRulesStorage.value)

    return responseData
  }

  /**
   * §13.1 — kembali ke akun admin. Backend menerbitkan token admin BARU
   * (bukan mengembalikan token lama yang sudah pernah dipegang browser) —
   * lihat App\Services\ImpersonationService::stop().
   */
  const stopImpersonation = async () => {
    try {
      const data = await useApi('/api/v1/impersonate/stop').post({ body: {} })
      const responseData = data?.data ?? data

      const maxAge = 60 * 60 * 24 * 30

      userDataCookie.value = responseData.admin_user
      accessTokenCookie.value = responseData.token
      userAbilityRulesStorage.value = responseData.userAbilities ?? []

      document.cookie = `userData=${encodeURIComponent(JSON.stringify(responseData.admin_user))}; path=/; max-age=${maxAge}`
      document.cookie = `accessToken=${encodeURIComponent(responseData.token)}; path=/; max-age=${maxAge}`

      ability.update(userAbilityRulesStorage.value)

      return responseData
    } finally {
      impersonationInfo.value = null
    }
  }

  return {
    user,
    token,
    abilities,
    isAuthenticated,
    isImpersonating,
    impersonationInfo,
    login,
    logout,
    fetchUser,
    startImpersonation,
    stopImpersonation,
  }
})
